import {
    ChangeDetectionStrategy,
    Component,
    computed, effect, ElementRef,
    inject,
    input,
    SecurityContext,
    signal, viewChild,
    ViewEncapsulation
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { DrawerComponent, TooltipDirective } from '../../shared';
import { TailwindService } from '@seven1/angular-storyblok/tailwind';
import { IInfoBlok } from './info.model';
import { StoryblokService } from '@seven1/angular-storyblok/api';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
    selector: 'app-info-button',
    imports: [MatIconModule, TooltipDirective, DrawerComponent, NgClass],
    templateUrl: './info.component.html',
    styleUrl: './info.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        '[class]': 'getAllClasses()',
    },
})
export class InfoComponent {
    private _tailwind = inject(TailwindService);
    private _storyblok = inject(StoryblokService);
    private _sanitizer = inject(DomSanitizer);
    static nextId = 0;
    readonly tooltipId = `info-button-${InfoComponent.nextId++}`;
    block = input.required<IInfoBlok>();
    openButtonRef = viewChild<ElementRef<HTMLButtonElement>>('openButton');

    drawerActive = signal(false);
    constructor() {
        let hasMounted = false;
        let prev = this.drawerActive();

        effect(() => {
            const current = this.drawerActive();
            if (!hasMounted) {
                hasMounted = true;
                prev = current;
                return;
            }
            if (prev && !current) {
                this.openButtonRef()?.nativeElement.focus();
            }
            prev = current;
        });
    }

    renderedContent = computed<string | undefined | null>(() => {
        const content = this.block()?.content;
        if (content) {
            const renderedText = this._storyblok.richTextResolver.render(content);
            return renderedText
                ? this._sanitizer.sanitize(
                    SecurityContext.HTML,
                    Array.isArray(renderedText)
                        ? renderedText.join('')
                        : renderedText
                )
                : undefined;
        }
        return undefined;
    });

    getAllClasses = computed<string[]>(() => {
        const block = this.block();
        return [
            ...this._tailwind.getDisplayClasses(block),
            ...this._tailwind.getSpacingClasses(block),
        ];
    });

    getColorClass = computed<string[]>(() => {
        return this._tailwind.getTypographyClasses(this.block());
    });
}
